import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map, take, filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';

/**
 * Auth Guard
 * Protects routes that require authentication
 * Redirects to login page if user is not authenticated
 * Waits for Firebase auth initialization to prevent race conditions
 *
 * Also enforces email verification: a signed-in but unverified user is sent
 * to /verify/email rather than to login, since they hold valid credentials and
 * just need to finish. That route carries no guard, so this can't loop.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Wait for Firebase auth to finish loading before checking user state
    return combineLatest([
      this.authService.loading$,
      this.authService.currentUser$
    ]).pipe(
      filter(([loading]) => !loading), // Wait until loading is complete
      take(1),
      map(([_, user]) => {
        if (!user) {
          // Store the attempted URL for redirecting after login
          return this.router.createUrlTree(['/login'], {
            queryParams: { returnUrl: state.url }
          });
        }

        if (!user.emailVerified) {
          return this.router.createUrlTree(['/verify/email']);
        }

        return true;
      })
    );
  }
}
